import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LangContext } from "../../contexts/LangContext";
import { 
  HiArrowLeft, HiPlay, HiHeart, HiChat, HiEye, HiTrendingUp, 
  HiSparkles, HiLightBulb, HiChartBar, HiFire, HiStar,
  HiDownload, HiLink, HiCalendar, HiClock, HiChevronDown, HiChevronUp
} from "react-icons/hi";
import { FaInstagram } from "react-icons/fa";
import en from "../../i18n/en";
import pl from "../../i18n/pl";

// ==================== MOCK DATA GENERATOR ====================
function generateMockReelsData() {
  const reelTemplates = [
    { 
      description: "Za kulisami naszego najnowszego launchu produktu 🚀 #BTS #ProductLaunch",
      views: 245000, likes: 42300, comments: 2800, 
      engagement_rate: 18.4, duration: 32, 
      thumbnail: "https://picsum.photos/seed/reel1/400/600",
      posted_at: "2026-06-20T14:30:00Z",
      url: "https://instagram.com/reel/example1",
      full_description: "Zajawka dnia premiery naszego produktu — od przygotowań o 6 rano do finałowego odsłonięcia. Pokazuje zespół przygotowujący miejsce, testujący dema i moment opadnięcia kurtyny. Surowe, nieedytowane, prawdziwe.",
      structure: {
        hook: "Zbliżenie na budzik o 6 rano z tekstem: 'Dzień premiery. Bez snu.'",
        body: "Szybki montaż: kawa, wieszanie banerów, próby dźwięku, nerwowe narady zespołu, potem odliczanie do odsłonięcia.",
        cta: "Nakładka tekstowa: 'To dopiero początek. Link w bio po wczesny dostęp.'"
      }
    }
  ];

  // Генерируем только 1 рил, но создаем 30 записей с одинаковыми данными для сохранения "Top 30" логики
  const singleReel = reelTemplates[0];
  return Array.from({ length: 30 }, (_, index) => ({
    id: `reel_${index + 1}`,
    rank: index + 1,
    ...singleReel,
    description: index === 0 ? singleReel.description : `Reel zastępczy #${index + 1}`,
    views: index === 0 ? Math.round(singleReel.views * (0.95 + Math.random() * 0.1)) : 0,
    likes: index === 0 ? Math.round(singleReel.likes * (0.95 + Math.random() * 0.1)) : 0,
    comments: index === 0 ? Math.round(singleReel.comments * (0.95 + Math.random() * 0.1)) : 0,
    engagement_rate: index === 0 ? Number((singleReel.engagement_rate * (0.97 + Math.random() * 0.06)).toFixed(1)) : 0,
    shares: index === 0 ? Math.round(singleReel.likes * (0.02 + Math.random() * 0.03)) : 0,
    saves: index === 0 ? Math.round(singleReel.likes * (0.05 + Math.random() * 0.05)) : 0,
  }));
}

function generateAISummary(reels) {
  const totalReels = 30; // Всегда показываем "30 reels"
  const avgViews = Math.round(reels.reduce((sum, r) => sum + r.views, 0) / totalReels);
  const avgEngagement = Number((reels.reduce((sum, r) => sum + r.engagement_rate, 0) / totalReels).toFixed(1));
  
  const top5 = reels.slice(0, 1); // Берем только первый рил для top5
  
  return {
    overall_trends: {
      total_reels_analyzed: totalReels,
      average_views: avgViews,
      average_engagement_rate: avgEngagement,
      total_views: reels.reduce((sum, r) => sum + r.views, 0),
    },
    top5_breakdown: top5.map(r => ({
      description: r.description,
      views: r.views,
      engagement_rate: r.engagement_rate,
      structure: r.structure,
      full_description: r.full_description
    })),
    top_performer_characteristics: {
      en: [
        `Hook within 1.5 seconds — top reel opened with bold text overlay ("${top5[0]?.structure?.hook?.substring(0, 40)}...") instead of logos or intros`,
        `Pattern interrupts every 4-6 seconds — the reel changed scenes/angles multiple times across its ${top5[0]?.duration}s duration, preventing scroll-away`,
        `Text overlays on 100% of frames — not relying on audio alone; captions burned into video`,
        `Emotional contrast — swung between tension and relief (struggle→breakthrough) within the first 10 seconds`,
        `Specific, quantified claims in hooks — "${top5[0]?.structure?.hook?.substring(0, 50)}" type openings outperformed vague promises`,
        `Comment-bait CTAs — ended with specific action request, not passive "link in bio"`,
        `Duration sweet spot: 28-44 seconds — this reel's ${top5[0]?.duration}s length falls in the optimal engagement range`
      ],
      pl: [
        `Hook w ciągu 1,5 sekundy — najlepszy reel otworzył się odważną nakładką tekstową ("${top5[0]?.structure?.hook?.substring(0, 40)}...") zamiast logo lub wprowadzenia`,
        `Przerywniki wzorca co 4-6 sekund — reel zmieniał sceny/perspektywy wielokrotnie w ciągu ${top5[0]?.duration}s, zapobiegając przewijaniu`,
        `Nakładki tekstowe na 100% klatek — nie polegano wyłącznie na audio; napisy wtopione w wideo`,
        `Kontrast emocjonalny — przechodził między napięciem a ulgą (walka→przełom) w ciągu pierwszych 10 sekund`,
        `Konkretne, wymierne twierdzenia w hookach — otwarcia typu "${top5[0]?.structure?.hook?.substring(0, 50)}" przewyższały niejasne obietnice`,
        `CTA zachęcające do komentarzy — zakończył się prośbą o konkretną akcję, nie pasywnym "link w bio"`,
        `Optymalny czas trwania: 28-44 sekundy — ${top5[0]?.duration}s tego reela mieści się w optymalnym zakresie zaangażowania`
      ]
    },
    content_recommendations: {
      en: [
        {
          category: "Tutorial & How-To Content",
          insight: `Tutorial content averaged 15% above overall average. Top performers used screen recording + face cam split-screen, not just screen alone.`,
          recommendation: "Increase tutorials to 3x/week. Mandatory format: problem statement in first 1.5s, split-screen showing face + screen, 3-5 rapid steps, end with downloadable resource (template/checklist) as comment-bait. Never start with 'Hey guys' or logo intro."
        },
        {
          category: "Behind-the-Scenes & Authentic",
          insight: `BTS content hit the highest engagement rates. Polished BTS flopped; iPhone-shot, unscripted moments with real team reactions drove 3x more shares.`,
          recommendation: "Allocate 30% of calendar to BTS. Rules: no lighting setups, no scripted lines, real office chaos shown. Best-performing format: 'day in the life' POV with timestamps, or 'how we actually [build/decide/fix]' showing real tools (Slack, Figma, terminal)."
        },
        {
          category: "Failure & Lessons Learned",
          insight: `Failure/lesson reels drove 40% above average views. Comments averaged 3.2x higher than other categories. Vulnerability with specific numbers ($ lost, time wasted) created trust.`,
          recommendation: "Post 1 failure story weekly. Required elements: specific cost (money or time), the moment you realized it failed (screenshot/data), and concrete lesson applied since. No generic 'we learned a lot' endings — show the change that happened after."
        }
      ],
      pl: [
        {
          category: "Treści instruktażowe i poradnikowe",
          insight: `Treści instruktażowe osiągały średnio 15% powyżej ogólnej średniej. Najlepsze wykorzystywały nagranie ekranu + kamerę twarzy w podzielonym ekranie, nie tylko sam ekran.`,
          recommendation: "Zwiększ liczbę poradników do 3x/tydzień. Obowiązkowy format: opis problemu w ciągu pierwszych 1,5s, podzielony ekran pokazujący twarz + ekran, 3-5 szybkich kroków, zakończ zasobem do pobrania (szablon/lista kontrolna) jako zachętą do komentarza. Nigdy nie zaczynaj od 'Hej' lub logo."
        },
        {
          category: "Za kulisami i autentyczność",
          insight: `Treści zza kulis osiągnęły najwyższe wskaźniki zaangażowania. Dopracowane BTS zawiodły; nieinscenizowane momenty kręcone iPhonem z prawdziwymi reakcjami zespołu generowały 3x więcej udostępnień.`,
          recommendation: "Przeznacz 30% kalendarza na BTS. Zasady: bez ustawiania oświetlenia, bez scenariusza, pokazuj prawdziwy chaos biurowy. Najlepszy format: POV 'dzień z życia' ze znacznikami czasu lub 'jak naprawdę [budujemy/decydujemy/naprawiamy]' pokazujący prawdziwe narzędzia (Slack, Figma, terminal)."
        },
        {
          category: "Porażki i wyciągnięte lekcje",
          insight: `Reele o porażkach/lekcjach osiągały 40% powyżej średniej wyświetleń. Komentarze średnio 3,2x wyższe niż w innych kategoriach. Wrażliwość z konkretnymi liczbami (stracone pieniądze, zmarnowany czas) budowała zaufanie.`,
          recommendation: "Publikuj 1 historię porażki tygodniowo. Wymagane elementy: konkretny koszt (pieniądze lub czas), moment, w którym zdałeś sobie sprawę z porażki (zrzut ekranu/dane) i konkretna lekcja zastosowana od tego czasu. Żadnych ogólnych zakończeń 'wiele się nauczyliśmy' — pokaż zmianę, która nastąpiła później."
        }
      ]
    },
    summary_text: {
      en: `ANALYSIS OF TOP 30 REELS — WHAT ACTUALLY WORKS

These 30 reels accumulated ${reels.reduce((sum, r) => sum + r.views, 0).toLocaleString()} total views at an average engagement rate of ${avgEngagement}%. Here's what separates the top performers from everything else.

HOOKS THAT ACTUALLY CONVERT:
The #1 pattern across top performers: the first 1.5 seconds contain either a bold text overlay making a specific claim, or immediate motion/action. Reels that started with logos, "hey guys," or slow pans lost 40% of viewers before second 3. Top hook formats: numbers-based claims ("We wasted $80K..."), pattern interrupts (sudden movement/sound), and vulnerability openers ("I was about to quit...").

CONTENT TYPES RANKED BY PERFORMANCE:
1. Failures & lessons — specific, quantified failures with concrete lessons
2. BTS/Authentic — iPhone-shot, real moments, zero polish
3. Community reactions — responding to criticism publicly
4. Tutorials — split-screen face + screen recording, under 45 seconds
5. AI experiments — showing limitations, not just magic

WHAT DOESN'T WORK (even though everyone does it):
• Polished, commercial-style reels — consistently bottom quartile
• Audio-dependent content without burned-in captions — 85% watch without sound
• Passive CTAs ("link in bio") — comment-bait CTAs drive 4x more comments
• Reels over 55 seconds — drop-off cliff at 45 seconds unless it's a reaction/hot take format

BOTTOM LINE:
Instagram in 2026 rewards specific, honest, and immediately valuable content. The top reel told the truth about something — a failure, a real morning routine, an actual customer story. No polish. Under 45 seconds. Text overlays throughout. And asked for a specific action in the comments, not bio.`,
      pl: `ANALIZA 30 NAJLEPSZYCH REELI — CO NAPRAWDĘ DZIAŁA

Te 30 reeli zgromadziło łącznie ${reels.reduce((sum, r) => sum + r.views, 0).toLocaleString()} wyświetleń przy średnim wskaźniku zaangażowania ${avgEngagement}%. Oto co wyróżnia najlepszych od całej reszty.

HOOKI, KTÓRE NAPRAWDĘ KONWERTUJĄ:
Wzorzec nr 1 wśród najlepszych: pierwsze 1,5 sekundy zawiera albo odważną nakładkę tekstową z konkretnym twierdzeniem, albo natychmiastowy ruch/akcję. Reele zaczynające się od logo, "hej" lub powolnych panoram traciły 40% widzów przed 3 sekundą. Najlepsze formaty hooków: twierdzenia oparte na liczbach ("Zmarnowaliśmy 80 tysięcy..."), przerywniki wzorca (nagły ruch/dźwięk) i otwarcia oparte na wrażliwości ("Miałem zamiar rzucić...").

TYPY TREŚCI USZEREGOWANE WEDŁUG SKUTECZNOŚCI:
1. Porażki i lekcje — konkretne, wymierne porażki z konkretnymi lekcjami
2. BTS/Autentyczność — kręcone iPhonem, prawdziwe momenty, zero dopracowania
3. Reakcje społeczności — publiczne odpowiadanie na krytykę
4. Poradniki — podzielony ekran twarz + nagranie ekranu, poniżej 45 sekund
5. Eksperymenty AI — pokazywanie ograniczeń, nie tylko magii

CO NIE DZIAŁA (mimo że wszyscy to robią):
• Dopracowane, komercyjne reele — konsekwentnie w dolnym kwartylu
• Treści zależne od audio bez wtopionych napisów — 85% ogląda bez dźwięku
• Pasywne CTA ("link w bio") — CTA zachęcające do komentarzy generują 4x więcej komentarzy
• Reele powyżej 55 sekund — gwałtowny spadek przy 45 sekundach, chyba że to format reakcji/gorących tematów

KONKLUZJA:
Instagram w 2026 roku nagradza konkretne, szczere i natychmiast wartościowe treści. Najlepszy reel powiedział prawdę o czymś — porażce, prawdziwej porannej rutynie, autentycznej historii klienta. Bez dopracowania. Poniżej 45 sekund. Nakładki tekstowe przez całość. I poprosił o konkretną akcję w komentarzach, nie w bio.`
    },
    generated_at: new Date().toISOString(),
    ai_model: "GPT-4o",
  };
}

// ==================== UTILS ====================
function formatNumber(num) {
  if (!num) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString();
}

function formatDate(dateStr, lang = 'en') {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const locale = lang === 'pl' ? 'pl-PL' : 'en-US';
  return d.toLocaleDateString(locale, { 
    month: "short", 
    day: "numeric",
    year: "numeric" 
  });
}

// ==================== REEL CARD ====================
function ReelCard({ reel, rank, isMobile, t, lang }) {
  const [imgError, setImgError] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const getRankBadge = () => {
    if (rank === 1) return { icon: "👑", bg: "#fef3c7", color: "#92400e", label: "#1" };
    if (rank === 2) return { icon: "🥈", bg: "#f1f5f9", color: "#475569", label: "#2" };
    if (rank === 3) return { icon: "🥉", bg: "#fef2f2", color: "#991b1b", label: "#3" };
    return { bg: "#f3f4f6", color: "#6b7280", label: `#${rank}` };
  };

  const badge = getRankBadge();

  return (
    <div style={{
      background: "#fff",
      borderRadius: 12,
      border: rank <= 3 ? "2px solid rgba(59,113,254,0.2)" : "1.5px solid var(--border)",
      overflow: "hidden",
      transition: ".15s",
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
    }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 20px rgba(59,113,254,.1)";
        e.currentTarget.style.borderColor = "var(--blue)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = rank <= 3 ? "rgba(59,113,254,0.2)" : "var(--border)";
      }}
    >
      {/* Thumbnail */}
      <div style={{
        width: isMobile ? "100%" : 160,
        height: isMobile ? 200 : 160,
        background: !imgError ? `url(${reel.thumbnail}) center/cover no-repeat` : "#f3f4f6",
        position: "relative",
        flexShrink: 0,
      }}
        onError={() => setImgError(true)}
      >
        {/* Rank Badge */}
        <div style={{
          position: "absolute", top: 10, left: 10,
          background: badge.bg, color: badge.color,
          borderRadius: 8, padding: "4px 10px",
          fontSize: 13, fontWeight: 700,
          display: "flex", alignItems: "center", gap: 4,
          border: "1px solid rgba(0,0,0,0.1)"
        }}>
          {badge.icon && <span style={{ fontSize: 14 }}>{badge.icon}</span>}
          {badge.label}
        </div>

        {/* Duration */}
        <div style={{
          position: "absolute", bottom: 10, right: 10,
          background: "rgba(0,0,0,0.7)", color: "#fff",
          borderRadius: 6, padding: "3px 8px",
          fontSize: 11, fontWeight: 600,
          display: "flex", alignItems: "center", gap: 3
        }}>
          <HiPlay size={10} /> 0:{reel.duration}
        </div>

        {imgError && (
          <div style={{
            width: "100%", height: "100%",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#9ca3af", fontSize: 40
          }}>
            <FaInstagram />
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: isMobile ? "14px 16px" : "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Description */}
        <div>
          <p style={{ 
            fontSize: 14, fontWeight: 600, color: "var(--dark)", 
            lineHeight: 1.5, margin: 0, marginBottom: 8,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            overflow: "hidden"
          }}>
            {reel.description}
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--muted)" }}>
            <HiCalendar size={12} />
            <span>{formatDate(reel.posted_at, lang)}</span>
          </div>
        </div>

        {/* Metrics */}
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : "repeat(4, 1fr)", 
          gap: 8 
        }}>
          <div style={{ textAlign: "center", padding: "8px 6px", borderRadius: 8, background: "#f0f4ff" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, color: "var(--blue)", marginBottom: 3 }}>
              <HiPlay size={14} /> <span style={{ fontSize: 10, fontWeight: 600 }}>{t.views}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--dark)" }}>
              {formatNumber(reel.views)}
            </div>
          </div>
          <div style={{ textAlign: "center", padding: "8px 6px", borderRadius: 8, background: "#fef2f2" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, color: "#ef4444", marginBottom: 3 }}>
              <HiHeart size={14} /> <span style={{ fontSize: 10, fontWeight: 600 }}>{t.likes}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--dark)" }}>
              {formatNumber(reel.likes)}
            </div>
          </div>
          <div style={{ textAlign: "center", padding: "8px 6px", borderRadius: 8, background: "#f0fdf4" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, color: "#22c55e", marginBottom: 3 }}>
              <HiChat size={14} /> <span style={{ fontSize: 10, fontWeight: 600 }}>{t.comments}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--dark)" }}>
              {formatNumber(reel.comments)}
            </div>
          </div>
          <div style={{ textAlign: "center", padding: "8px 6px", borderRadius: 8, background: "#fefce8" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, color: "#eab308", marginBottom: 3 }}>
              <HiTrendingUp size={14} /> <span style={{ fontSize: 10, fontWeight: 600 }}>{t.engagement}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--dark)" }}>
              {reel.engagement_rate}%
            </div>
          </div>
        </div>

        {/* Action Buttons Row */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: "auto" }}>
          <a 
            href={reel.url} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: 12, fontWeight: 600, color: "var(--blue)",
              textDecoration: "none",
              padding: "6px 0"
            }}
            onMouseEnter={(e) => e.currentTarget.style.textDecoration = "underline"}
            onMouseLeave={(e) => e.currentTarget.style.textDecoration = "none"}
          >
            <HiLink size={14} /> {t.viewOnInstagram}
          </a>
          
          <button
            onClick={() => setExpanded(!expanded)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: 12, fontWeight: 600,
              color: "var(--muted)",
              background: "none", border: "none", cursor: "pointer",
              padding: "6px 8px", borderRadius: 6,
              transition: ".15s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f3f4f6";
              e.currentTarget.style.color = "var(--dark)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "none";
              e.currentTarget.style.color = "var(--muted)";
            }}
          >
            {expanded ? <HiChevronUp size={14} /> : <HiChevronDown size={14} />}
            {expanded ? t.hideDescription : t.showDescription}
          </button>
        </div>

        {/* Expandable Description */}
        {expanded && (
          <div style={{
            background: "#f8fafc",
            borderRadius: 10,
            border: "1px solid #e2e8f0",
            padding: "16px",
            marginTop: 4
          }}>
            {/* Full Description */}
            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: "#6366f1",
                textTransform: "uppercase", letterSpacing: .5,
                marginBottom: 8
              }}>
                {t.fullDescription || "Co się dzieje w tym Reelu"}
              </div>
              <p style={{
                fontSize: 13, color: "#334155", lineHeight: 1.7, margin: 0
              }}>
                {reel.full_description}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== AI SUMMARY SECTION ====================
function AISummarySection({ summary, isMobile, t, lang }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{
      background: "#fff",
      borderRadius: 16,
      border: "1.5px solid rgba(99,102,241,0.3)",
      overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        padding: "20px 24px",
        background: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)",
        borderBottom: "1px solid #c7d2fe",
        display: "flex",
        alignItems: "flex-start",
        gap: 14
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontSize: 24, flexShrink: 0
        }}>
          🤖
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e1b4b", margin: "0 0 4px 0" }}>
            {t.aiSummary}
          </h3>
          <p style={{ fontSize: 13, color: "#6366f1", margin: 0 }}>
            {summary.ai_model} · {formatDate(summary.generated_at, lang)}
          </p>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            padding: "8px 14px", borderRadius: 8,
            border: "1.5px solid #c7d2fe",
            background: "#fff", cursor: "pointer",
            fontSize: 12, fontWeight: 600, color: "#4f46e5",
            whiteSpace: "nowrap", transition: ".15s",
            flexShrink: 0
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = "#eef2ff"}
          onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
        >
          {expanded ? t.showLess : t.showFullAnalysis}
        </button>
      </div>

      {/* Expanded Analysis */}
      {expanded && (
        <div style={{ padding: "0 24px 24px", borderTop: "1px solid var(--border)" }}>
          {/* Summary Text */}
          <div style={{
            background: "#f8fafc",
            borderRadius: 10,
            border: "1px solid #e2e8f0",
            padding: "18px 20px",
            marginBottom: 20,
            marginTop: 20
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: "#6366f1",
              textTransform: "uppercase", letterSpacing: .8,
              marginBottom: 10, display: "flex", alignItems: "center", gap: 6
            }}>
              <HiSparkles size={14} /> {t.comprehensiveAnalysis}
            </div>
            <p style={{
              fontSize: 14, color: "#334155", lineHeight: 1.8,
              whiteSpace: "pre-wrap", margin: 0
            }}>
              {summary.summary_text[lang] || summary.summary_text.en}
            </p>
          </div>

          {/* Top Performer Characteristics */}
          <div style={{ marginBottom: 20 }}>
            <h4 style={{
              fontSize: 14, fontWeight: 700, color: "var(--dark)",
              marginBottom: 14, display: "flex", alignItems: "center", gap: 8
            }}>
              <HiFire size={18} color="#f59e0b" />
              {t.topPerformerCharacteristics}
            </h4>
            <div style={{ display: "grid", gap: 8 }}>
              {(summary.top_performer_characteristics[lang] || summary.top_performer_characteristics.en).map((char, idx) => (
                <div key={idx} style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "12px 16px", borderRadius: 10,
                  background: "#fafbff", border: "1.5px solid var(--border)",
                  fontSize: 13, color: "#334155"
                }}>
                  <span style={{
                    width: 24, height: 24, borderRadius: 6,
                    background: "rgba(59,113,254,0.1)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "var(--blue)", fontSize: 11, fontWeight: 700,
                    flexShrink: 0
                  }}>
                    {idx + 1}
                  </span>
                  {char}
                </div>
              ))}
            </div>
          </div>

          {/* Content Recommendations */}
          <div>
            <h4 style={{
              fontSize: 14, fontWeight: 700, color: "var(--dark)",
              marginBottom: 14, display: "flex", alignItems: "center", gap: 8
            }}>
              <HiLightBulb size={18} color="#f59e0b" />
              {t.contentRecommendations}
            </h4>
            <div style={{ display: "grid", gap: 12 }}>
              {(summary.content_recommendations[lang] || summary.content_recommendations.en).map((rec, idx) => (
                <div key={idx} style={{
                  padding: "16px", borderRadius: 10,
                  background: "#fafbff", border: "1.5px solid var(--border)"
                }}>
                  <h5 style={{
                    fontSize: 14, fontWeight: 700, color: "var(--dark)",
                    margin: "0 0 8px 0"
                  }}>
                    {rec.category}
                  </h5>
                  <p style={{
                    fontSize: 13, color: "#64748b", lineHeight: 1.6, margin: "0 0 10px 0"
                  }}>
                    {rec.insight}
                  </p>
                  <p style={{
                    fontSize: 13, color: "#334155", lineHeight: 1.6, margin: 0,
                    fontWeight: 500
                  }}>
                    {rec.recommendation}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== MAIN PAGE COMPONENT ====================
export function ReelsResultsPage() {
  const { lang } = React.useContext(LangContext);
  const fullT = lang === "pl" ? pl : en;
  const t = fullT.reelsResultsPage || {};
  const navigate = useNavigate();
  const { taskId } = useParams();
  
  const [reels, setReels] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("engagement_rate");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      const generatedReels = generateMockReelsData();
      setReels(generatedReels);
      setSummary(generateAISummary(generatedReels));
      setLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, [taskId]);

  const sortedReels = useMemo(() => {
    return [...reels].sort((a, b) => {
      switch (sortBy) {
        case "views": return b.views - a.views;
        case "likes": return b.likes - a.likes;
        case "comments": return b.comments - a.comments;
        case "engagement_rate": return b.engagement_rate - a.engagement_rate;
        default: return b.engagement_rate - a.engagement_rate;
      }
    });
  }, [reels, sortBy]);

  if (loading) {
    return (
      <div style={{ 
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", 
        padding: "80px 20px", gap: 20 
      }}>
        <div className="spinner" />
        <div style={{ textAlign: "center" }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "var(--dark)", marginBottom: 6 }}>
            {t.loadingResults || "Analizowanie Reeli..."}
          </h3>
          <p style={{ fontSize: 13, color: "var(--muted)" }}>
            {t.generatingAISummary || "Generowanie podsumowania AI i ranking najlepszych"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Back Button + Header */}
      <div style={{
        display: "flex", alignItems: "flex-start", gap: 16,
        marginBottom: 24
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: 10, borderRadius: 10,
            border: "1.5px solid var(--border)",
            background: "#fff", cursor: "pointer",
            color: "var(--muted)", display: "flex",
            alignItems: "center", transition: ".15s",
            marginTop: 2
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#f3f4f6";
            e.currentTarget.style.borderColor = "var(--blue)";
            e.currentTarget.style.color = "var(--blue)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#fff";
            e.currentTarget.style.borderColor = "var(--border)";
            e.currentTarget.style.color = "var(--muted)";
          }}
        >
          <HiArrowLeft size={20} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <FaInstagram size={20} color="#E4405F" />
            <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: "var(--dark)", margin: 0 }}>
              {t.reelsResults || "Wyniki Instagram Reels"}
            </h2>
          </div>
          <p style={{ fontSize: 13, color: "var(--muted)", margin: 0 }}>
            {t.topReelsAnalysis || "Top 30 najlepszych reeli z analizą AI i rekomendacjami"}
            {taskId && <span> · Zadanie #{taskId}</span>}
          </p>
        </div>

        {/* Sort Controls */}
        <div style={{ display: "flex", gap: 4, flexShrink: 0, flexWrap: "wrap" }}>
          {[
            { key: "engagement_rate", label: t.engagement || "Zaangażowanie" },
            { key: "views", label: t.views || "Wyświetlenia" },
            { key: "likes", label: t.likes || "Polubienia" },
            { key: "comments", label: t.comments || "Komentarze" },
          ].map((sort) => (
            <button
              key={sort.key}
              onClick={() => setSortBy(sort.key)}
              style={{
                padding: "7px 14px", borderRadius: 8,
                border: `1.5px solid ${sortBy === sort.key ? "var(--blue)" : "var(--border)"}`,
                background: sortBy === sort.key ? "rgba(59,113,254,0.05)" : "#fff",
                cursor: "pointer", fontSize: 12, fontWeight: 600,
                color: sortBy === sort.key ? "var(--blue)" : "var(--muted)",
                transition: ".15s", whiteSpace: "nowrap"
              }}
            >
              {sort.label}
            </button>
          ))}
        </div>
      </div>

      {/* AI Summary */}
      <div style={{ marginBottom: 28 }}>
        <AISummarySection summary={summary} isMobile={isMobile} t={t} lang={lang} />
      </div>

      {/* Reels Grid */}
      <div>
        <h3 style={{
          fontSize: 16, fontWeight: 700, color: "var(--dark)",
          marginBottom: 16, display: "flex", alignItems: "center", gap: 8
        }}>
          <HiChartBar size={18} color="var(--blue)" />
          {t.topReels || "Top 30 Reeli"} ({reels.length})
        </h3>
        <div style={{ display: "grid", gap: 12 }}>
          {sortedReels.map((reel, index) => (
            <ReelCard 
              key={reel.id} 
              reel={reel} 
              rank={index + 1} 
              isMobile={isMobile} 
              t={t}
              lang={lang}
            />
          ))}
        </div>
      </div>
    </div>
  );
}